import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { NotificationItem } from '@app/core/interfaces/common.interface';
import {
  NOTIFICATION_API_QUERY_PARAMS,
  NOTIFICATION_COUNTRY_IDS,
  NOTIFICATION_DEFAULTS,
  NOTIFICATION_TEXT,
} from '@app/core/constants/notification.constants';
import {
  NOTIFICATION_COUNTRY_DISPLAY_GROUPS,
  NOTIFICATION_COUNTRY_NAME_NORMALIZATION,
} from '@app/core/constants/notifications.constants';
import {
  NotificationApiRecord,
  readNotificationBoolean,
  readNotificationNumber,
  readNotificationValue,
  toNotificationDisplayTime,
} from '@app/utils/notification.utils';
import {
  CreateNotificationRequest,
  CreateNotificationResponse,
  UpdateNotificationRequest,
} from '@app/core/interfaces/notification-api.interface';
import { environment } from 'src/environments/environment';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';

type UnreadCountResponse =
  | number
  | {
      unreadCount?: number;
      count?: number;
      data?: {
        unreadCount?: number;
        count?: number;
      };
      Success?: boolean;
      Message?: string | null;
      Status?: string;
      Data?: number;
    };

type NotificationListEnvelope = {
  data?: NotificationApiRecord[] | NotificationListEnvelope;
  Data?: NotificationApiRecord[] | NotificationListEnvelope;
  items?: NotificationApiRecord[];
  Items?: NotificationApiRecord[];
  TotalCount?: number;
  totalCount?: number;
  Total?: number;
  total?: number;
  TotalRecords?: number;
  totalRecords?: number;
  Success?: boolean;
  Message?: string | null;
  Status?: string;
};

type NotificationListResponse = NotificationApiRecord[] | NotificationListEnvelope;

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  // ─── Public reactive state (consumed by components via signals) ────────────

  /** Raw list of mapped notification items for the all notifications page. */
  readonly notificationsState = signal<NotificationItem[]>([]);

  /** Raw list of mapped notification items for the dropdown (unread only). */
  readonly dropdownNotificationsState = signal<NotificationItem[]>([]);

  /** Derived read-only view of the all notifications list. */
  readonly notifications = computed(() => this.notificationsState());

  /** Derived read-only view of the dropdown notification list. */
  readonly dropdownNotifications = computed(() => this.dropdownNotificationsState());

  /** Derived count of unread notifications. */
  readonly notificationCount = computed(() => this.unreadCountState());

  /** Whether an all notifications page HTTP request is in-flight. */
  readonly isNotificationsLoading = computed(() => this.notificationsLoadingState());

  /** Whether a dropdown notifications HTTP request is in-flight. */
  readonly isDropdownNotificationsLoading = computed(() =>
    this.dropdownNotificationsLoadingState(),
  );

  /** Total number of notification records returned by the last API response. */
  readonly totalNotificationRecords = computed(() => this.totalRecordsState());

  // ─── Private state ─────────────────────────────────────────────────────────

  private readonly http = inject(HttpClient);

  /** Base API URL for notification endpoints. */
  private readonly baseUrl = environment.apiUrl ?? '';

  /** Full endpoint for unread count API. */
  private readonly unreadCountApiUrl = `${this.baseUrl}notification/unread-count`;

  /** Full endpoint for notifications list API. */
  private readonly notificationsApiUrl = `${this.baseUrl}notification/notifications`;

  /** Full endpoint for mark-as-read API. */
  private readonly markAsReadApiUrl = `${this.baseUrl}notification/mark-as-read`;

  /** Full endpoint for clear-all API. */
  private readonly clearAllApiUrl = `${this.baseUrl}notification/clear-all`;

  /** Full endpoint for creating a new notification. */
  private readonly createNotificationApiUrl = `${this.baseUrl}notification`;

  /** Full endpoint for deleting a notification. */
  private readonly deleteNotificationApiUrl = `${this.baseUrl}notification`;

  /** Full endpoint for admin notifications list. */
  private readonly adminNotificationsApiUrl = `${this.baseUrl}notification/admin`;

  /** Number of unread notifications. Updated after fetch and mark-as-read. */
  private readonly unreadCountState = signal(0);

  /** IDs of notifications not yet marked as read; used for the batch mark-as-read call. */
  private readonly unreadNotificationIdsState = signal<number[]>([]);

  /** IDs of notifications in the current all notifications page list; used for clear-all payload. */
  private readonly notificationIdsState = signal<number[]>([]);

  /** IDs of notifications in the current dropdown list; used for clear-all payload. */
  private readonly dropdownNotificationIdsState = signal<number[]>([]);

  /** Last all notifications page query context used to refresh list after clear-all. */
  private readonly lastPageQueryState = signal<{
    countryId: number | null;
    page: number;
    pageSize: number;
    userId?: number;
    search?: string;
  }>({
    countryId: null,
    page: NOTIFICATION_DEFAULTS.DEFAULT_PAGE,
    pageSize: NOTIFICATION_DEFAULTS.DEFAULT_PAGE_SIZE,
    search: '',
  });

  /** Last dropdown notifications query context used to refresh list after clear-all. */
  private readonly lastDropdownQueryState = signal<{
    countryId: number | null;
    pageSize: number;
    userId?: number;
  }>({
    countryId: null,
    pageSize: NOTIFICATION_DEFAULTS.DROPDOWN_PAGE_SIZE,
    userId: undefined,
  });

  /** Tracks whether a `getNotifications` request is active. */
  private readonly notificationsLoadingState = signal(false);

  /** Tracks whether a `getDropdownNotifications` request is active. */
  private readonly dropdownNotificationsLoadingState = signal(false);

  /** Total record count reported by the last paginated API response. */
  private readonly totalRecordsState = signal(0);

  // ─── Public API ────────────────────────────────────────────────────────────

  /**
   * Fetches the current unread notification count for the given country.
   * Updates `unreadCountState` on success, resets to 0 on error.
   */
  /**
   * Creates a new notification via POST /notification.
   * Returns the raw API response so callers can react to success/failure.
   */
  createNotification(payload: CreateNotificationRequest): Observable<CreateNotificationResponse> {
    return this.http.post<CreateNotificationResponse>(this.createNotificationApiUrl, payload);
  }

  /**
   * Deletes a notification by id via DELETE /notification/{notificationId}.
   * The API does not expose a response body, so the observable resolves to void.
   */
  deleteNotification(notificationId: string): Observable<void> {
    return this.http.delete<void>(`${this.deleteNotificationApiUrl}/${notificationId}`);
  }

  // Update notification
  updateNotification(
    id: string,
    payload: UpdateNotificationRequest,
  ): Observable<CreateNotificationResponse> {
    return this.http.patch<CreateNotificationResponse>(
      `${this.createNotificationApiUrl}/${id}`,
      payload,
    );
  }

  getUnreadCount(countryId: number | null, userId?: number): Observable<number> {
    const params = this.buildBaseParams(countryId, undefined, userId);

    return this.http.get<UnreadCountResponse>(this.unreadCountApiUrl, { params }).pipe(
      map((res) => this.mapUnreadCount(res)),
      tap((count) => this.unreadCountState.set(count)),
      catchError(() => {
        this.unreadCountState.set(0);
        return of(0);
      }),
    );
  }

  /**
   * Fetches a paginated list of notifications for the given country.
   * Sets `notificationsLoadingState` to true at the start and false in `finalize`.
   * Updates `notificationsState`, `unreadNotificationIdsState`, and `totalRecordsState`
   * on success; resets them to empty/zero on error.
   *
   * @param countryId - Country used to filter notifications and resolve timezones.
   * @param page      - 1-based page number sent directly to the API.
   * @param pageSize  - Number of items per page.
   * @param userId    - Optional user scope for CNH/Admin impersonation.
   */
  getNotifications(
    countryId: number | null,
    page: number = NOTIFICATION_DEFAULTS.DEFAULT_PAGE,
    pageSize: number = NOTIFICATION_DEFAULTS.DEFAULT_PAGE_SIZE,
    userId?: number,
    search?: string,
  ): Observable<NotificationItem[]> {
    return this.fetchPaginatedNotifications(
      this.notificationsApiUrl,
      countryId,
      page,
      pageSize,
      userId,
      search,
    );
  }

  getAdminNotifications(
    countryId: number | null,
    page: number = NOTIFICATION_DEFAULTS.DEFAULT_PAGE,
    pageSize: number = NOTIFICATION_DEFAULTS.DEFAULT_PAGE_SIZE,
    userId?: number,
    search?: string,
  ): Observable<NotificationItem[]> {
    return this.fetchPaginatedNotifications(
      this.adminNotificationsApiUrl,
      countryId,
      page,
      pageSize,
      userId,
      search,
    );
  }

  /**
   * Fetches unread notifications for the dropdown.
   * Always appends `isRead=false` and writes only dropdown-specific state.
   */
  getDropdownNotifications(
    countryId: number | null,
    pageSize: number = NOTIFICATION_DEFAULTS.DROPDOWN_PAGE_SIZE,
    userId?: number,
  ): Observable<NotificationItem[]> {
    this.dropdownNotificationsLoadingState.set(true);
    this.lastDropdownQueryState.set({ countryId, pageSize, userId });

    const params = this.buildBaseParams(countryId, false, userId)
      .set(NOTIFICATION_API_QUERY_PARAMS.PAGE, String(NOTIFICATION_DEFAULTS.DEFAULT_PAGE))
      .set(NOTIFICATION_API_QUERY_PARAMS.PAGE_SIZE, String(pageSize));

    return this.http.get<NotificationListResponse>(this.notificationsApiUrl, { params }).pipe(
      map((response) => ({
        items: this.extractNotificationItems(response),
        total: this.extractTotalCount(response),
      })),
      map(({ items, total }) => ({
        mappedItems: items.map((item, index) => this.mapNotificationItem(item, index, countryId)),
        notificationIds: this.extractNotificationIds(items),
        unreadIds: this.extractUnreadNotificationIds(items),
        total,
      })),
      tap(({ mappedItems, notificationIds, unreadIds, total }) => {
        this.dropdownNotificationsState.set(mappedItems);
        this.dropdownNotificationIdsState.set(notificationIds);
        this.unreadNotificationIdsState.set(unreadIds);
        this.unreadCountState.set(total || mappedItems.length);
      }),
      map(({ mappedItems }) => mappedItems),
      catchError(() => {
        this.dropdownNotificationsState.set([]);
        this.dropdownNotificationIdsState.set([]);
        this.unreadNotificationIdsState.set([]);
        this.unreadCountState.set(0);
        return of([]);
      }),
      finalize(() => this.dropdownNotificationsLoadingState.set(false)),
    );
  }

  /**
   * Sends a PATCH request to mark all currently unread notifications as read.
   * If there are no unread IDs, it short-circuits and resets the count locally.
   */
  markAsRead(scope: 'dropdown' | 'page' = 'dropdown'): Observable<void> {
    const notificationIds =
      scope === 'dropdown'
        ? this.unreadNotificationIdsState()
        : this.extractUnreadNotificationIdsFromMappedItems(this.notificationsState());

    // Nothing to mark — reset count and skip the network call.
    if (!notificationIds.length) {
      this.unreadCountState.set(0);
      return of(undefined);
    }

    return this.http.patch<unknown>(this.markAsReadApiUrl, { notificationIds }).pipe(
      tap(() => {
        this.unreadCountState.set(0);
        if (scope === 'dropdown') {
          this.unreadNotificationIdsState.set([]);
        }
      }),
      map(() => undefined),
      catchError(() => {
        // On failure still clear local state so the badge does not stay stale.
        this.unreadCountState.set(0);
        if (scope === 'dropdown') {
          this.unreadNotificationIdsState.set([]);
        }
        return of(undefined);
      }),
    );
  }

  /**
   * Clears all notifications for the current notification list.
   * Sends PATCH payload: `{ notificationIds: number[] }`.
   * Keeps local UI state consistent even if API call fails.
   */
  clearAll(scope: 'dropdown' | 'page' = 'dropdown', onSuccess?: () => void): void {
    if (scope === 'dropdown') {
      this.clearDropdownNotifications(onSuccess);
      return;
    }

    this.clearPageNotifications(onSuccess);
  }

  /** Resets only the unread badge count without clearing the notification list. */
  resetUnreadCount(): void {
    this.unreadCountState.set(0);
  }

  // ─── Private helpers: Request Building ─────────────────────────────────────

  private fetchPaginatedNotifications(
    url: string,
    countryId: number | null,
    page: number,
    pageSize: number,
    userId?: number,
    search?: string,
  ): Observable<NotificationItem[]> {
    this.notificationsLoadingState.set(true);
    this.lastPageQueryState.set({ countryId, page, pageSize, userId, search });

    let params = this.buildBaseParams(countryId, undefined, userId)
      .set(NOTIFICATION_API_QUERY_PARAMS.PAGE, String(page))
      .set(NOTIFICATION_API_QUERY_PARAMS.PAGE_SIZE, String(pageSize));

    if (search?.trim()) {
      params = params.set(NOTIFICATION_API_QUERY_PARAMS.SEARCH, search.trim());
    }

    return this.http.get<NotificationListResponse>(url, { params }).pipe(
      map((response) => ({
        items: this.extractNotificationItems(response),
        total: this.extractTotalCount(response),
      })),
      map(({ items, total }) => ({
        mappedItems: items.map((item, index) => this.mapNotificationItem(item, index, countryId)),
        notificationIds: this.extractNotificationIds(items),
        unreadIds: this.extractUnreadNotificationIds(items),
        total,
      })),
      tap(({ mappedItems, notificationIds, unreadIds, total }) => {
        this.notificationsState.set(mappedItems);
        this.notificationIdsState.set(notificationIds);
        this.unreadNotificationIdsState.set(unreadIds);
        this.totalRecordsState.set(total);
      }),
      map(({ mappedItems }) => mappedItems),
      catchError(() => {
        this.notificationsState.set([]);
        this.notificationIdsState.set([]);
        this.unreadNotificationIdsState.set([]);
        this.totalRecordsState.set(0);
        return of([]);
      }),
      finalize(() => this.notificationsLoadingState.set(false)),
    );
  }

  /**
   * Builds the base `HttpParams` object shared by all API calls.
   * Appends the country-id param only when a non-null value is provided.
   * Appends the isRead param only when a boolean value is provided.
   * User-scoped requests are marked as impersonated for the API.
   * This ensures all notification requests are scoped to the correct country.
   */
  private buildBaseParams(countryId: number | null, isRead?: boolean, userId?: number): HttpParams {
    let params = new HttpParams();

    if (countryId !== null) {
      params = params.set(NOTIFICATION_API_QUERY_PARAMS.COUNTRY_ID, String(countryId));
    }

    if (userId !== undefined) {
      params = params.set(NOTIFICATION_API_QUERY_PARAMS.USER_ID, String(userId));
      params = params.set(NOTIFICATION_API_QUERY_PARAMS.IS_IMPERSONATING, 'true');
    }

    if (isRead !== undefined) {
      params = params.set(NOTIFICATION_API_QUERY_PARAMS.IS_READ, String(isRead));
    }

    return params;
  }

  // ─── Private helpers: Response Parsing ──────────────────────────────────────

  /**
   * Normalises the unread-count response, which may arrive as a plain number
   * or nested inside various envelope shapes. Extracts the numeric count
   * from any recognized response format.
   */
  private mapUnreadCount(res: UnreadCountResponse): number {
    if (typeof res === 'number') {
      return res;
    }

    return Number(res.Data ?? 0);
  }

  /**
   * Extracts the total record count from a notification list response.
   * Handles both flat and nested response structures by checking at the top level
   * first, then searching within Data/data objects if needed.
   * Returns the response length if response is a plain array, or 0 if not found.
   */
  private extractTotalCount(response: NotificationListResponse): number {
    if (Array.isArray(response)) {
      return response.length;
    }

    // Check top level first
    let total = this.getTotalCountFromObject(response);
    if (total !== null) {
      return total;
    }

    // If not found at top level, search in nested Data or data objects
    if (!this.isRecord(response)) {
      return 0;
    }

    const nestedObj = response.Data ?? response.data;
    if (this.isRecord(nestedObj)) {
      total = this.getTotalCountFromObject(nestedObj);
      return total ?? 0;
    }

    return 0;
  }

  /**
   * Extracts total count from an object by checking all possible field names.
   * Tries multiple field name variations to handle inconsistent API responses.
   */
  private getTotalCountFromObject(obj: unknown): number | null {
    if (!this.isRecord(obj)) {
      return null;
    }
    const value = obj['TotalCount'];

    if (value !== undefined && value !== null) {
      const numValue = Number(value);
      return Number.isFinite(numValue) ? numValue : 0;
    }

    return null;
  }

  /**
   * Extracts the flat array of raw notification records from a response that
   * may be a plain array or a deeply nested envelope object. Handles various
   * response shapes by delegating to specialized extraction methods.
   */
  private extractNotificationItems(response: NotificationListResponse): NotificationApiRecord[] {
    if (Array.isArray(response)) {
      return this.filterRecordArray(response);
    }

    return this.extractNestedItems(response.Data ?? response.Items);
  }

  /**
   * Recursively unwraps a potentially nested value until the inner array of
   * notification records is found. Returns an empty array if no array is found
   * or if the value is not a valid record.
   */
  private extractNestedItems(value: unknown): NotificationApiRecord[] {
    if (Array.isArray(value)) {
      return this.filterRecordArray(value);
    }

    if (!this.isRecord(value)) {
      return [];
    }

    // Recursively search for Items or Data properties until array is found
    return this.extractNestedItems(value['Items'] ?? value['Data']);
  }

  /**
   * Filters an unknown array down to only valid plain-object records,
   * discarding primitives, null values, and non-record objects.
   */
  private filterRecordArray(items: unknown[]): NotificationApiRecord[] {
    return items.filter((item): item is NotificationApiRecord => this.isRecord(item));
  }

  // ─── Private helpers: Data Extraction ───────────────────────────────────────

  /**
   * Extracts numeric notification IDs from the raw API records.
   * Returns array of IDs suitable for API requests.
   */
  private extractNotificationIds(items: NotificationApiRecord[]): number[] {
    return items
      .map((item) => readNotificationNumber(item, 'Id', 'id'))
      .filter((id): id is number => id !== null);
  }

  /**
   * Returns the numeric IDs of all unread notifications where `IsRead` / `isRead` is `false`.
   * These IDs are collected and batched into the mark-as-read request payload.
   */
  private extractUnreadNotificationIds(items: NotificationApiRecord[]): number[] {
    return items
      .filter((item) => readNotificationBoolean(item, 'IsRead') === false)
      .map((item) => readNotificationNumber(item, 'Id'))
      .filter((id): id is number => id !== null);
  }

  /**
   * Extracts numeric ids from mapped unread notification items.
   */
  private extractUnreadNotificationIdsFromMappedItems(items: NotificationItem[]): number[] {
    return items
      .filter((item) => !item.isRead)
      .map((item) => Number(item.id))
      .filter((id) => Number.isFinite(id));
  }

  /**
   * Returns notification IDs to be sent in the clear-all API request.
   * Prefers raw API IDs captured during `getNotifications` fetch; falls back to
   * currently mapped notification state IDs when the raw IDs are unavailable.
   */
  private getClearAllNotificationIds(): number[] {
    const idsFromApiPayload = this.notificationIdsState();
    if (idsFromApiPayload.length) {
      return idsFromApiPayload;
    }

    return this.notificationsState()
      .map((notification) => Number(notification.id))
      .filter((id) => Number.isFinite(id));
  }

  /**
   * Returns dropdown notification IDs to be sent in the clear-all API request.
   */
  private getDropdownClearAllNotificationIds(): number[] {
    const idsFromApiPayload = this.dropdownNotificationIdsState();
    if (idsFromApiPayload.length) {
      return idsFromApiPayload;
    }

    return this.dropdownNotificationsState()
      .map((notification) => Number(notification.id))
      .filter((id) => Number.isFinite(id));
  }

  /**
   * Maps a single raw API record to the `NotificationItem` view model.
   * Extracts fields using flexible field name matching to handle API inconsistencies.
   * Falls back to generated IDs and empty strings when API fields are absent.
   */
  private mapNotificationItem(
    item: NotificationApiRecord,
    index: number,
    selectedCountryId: number | null,
  ): NotificationItem {
    // Prefer the country stored on the notification; fall back to the selected one.
    const notificationCountryId = readNotificationNumber(item, 'CountryId') ?? selectedCountryId;

    // Try multiple field names because the API casing is inconsistent across environments.
    const publishedAt = readNotificationValue(
      item,
      'PublishedAt',
      'TimeAgo',
      'CreatedDate',
      'Date',
      'NotificationDate',
      'CreatedOn',
    );
    const createdDateMetadata = this.mapCreatedDateMetadata(publishedAt);
    const countryNames = this.mapNotificationCountryNames(item, selectedCountryId);

    return {
      id: readNotificationValue(item, 'Id', 'id') || `notification-${index + 1}`,
      title: readNotificationValue(item, 'Title') || NOTIFICATION_TEXT.FALLBACK_TITLE,
      shortDescription: readNotificationValue(item, 'ShortDescription') || '',
      fullDescription: readNotificationValue(item, 'FullDescription') || '',
      timeAgo: toNotificationDisplayTime(publishedAt),
      createdDateDisplay: createdDateMetadata.display,
      createdDateIso: createdDateMetadata.iso,
      countryNames,
      isRead: readNotificationBoolean(item, 'IsRead') ?? false,
      // Used by the notifications UI to decide whether to render the
      // "View details" action for Discover-specific notifications.
      notificationTypeName: readNotificationValue(
        item,
        'NotificationTypeName',
        'notificationTypeName',
      ),
      // Used by "View details" to open the exact Discover banner from a notification.
      bannerId: readNotificationValue(
        item,
        'BannerId',
        'bannerId',
        'MarketingBannerId',
        'marketingBannerId',
      ),
      // Stored so navigateToDiscover can scope banner searches to this country.
      countryId: notificationCountryId,
    };
  }

  /** Maps raw date-like values into stable created-date metadata for card rendering. */
  private mapCreatedDateMetadata(value: string): { display: string; iso?: string } {
    if (!value) {
      return { display: NOTIFICATION_TEXT.JUST_NOW };
    }

    const parsed = Date.parse(value);
    if (Number.isNaN(parsed)) {
      return { display: value };
    }

    const date = new Date(parsed);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return {
      display: `${day}-${month}-${year}`,
      iso: date.toISOString(),
    };
  }

  /** Resolves notification country names from API fields or selected country fallback. */
  private mapNotificationCountryNames(
    item: NotificationApiRecord,
    selectedCountryId: number | null,
  ): string[] {
    const countryNamesFromText = this.extractCountryNamesFromText(
      readNotificationValue(
        item,
        'CountryNames',
        'CountryName',
        'countryName',
        'Country',
        'country',
      ),
    );
    if (countryNamesFromText.length > 0) {
      return countryNamesFromText;
    }

    const countryId = readNotificationNumber(item, 'CountryId', 'countryId') ?? selectedCountryId;
    if (countryId === null) {
      return [];
    }

    if (countryId === NOTIFICATION_COUNTRY_IDS.ALL) {
      return [...NOTIFICATION_COUNTRY_DISPLAY_GROUPS[NOTIFICATION_COUNTRY_IDS.ALL]];
    }

    const mappedCountryNames = NOTIFICATION_COUNTRY_DISPLAY_GROUPS[countryId];
    return mappedCountryNames ? [...mappedCountryNames] : [];
  }

  /** Splits free-form country text and normalizes names to display labels. */
  private extractCountryNamesFromText(value: string): string[] {
    if (!value.trim()) {
      return [];
    }

    const tokens = value
      .split(/[;,|]/)
      .map((name) => name.trim())
      .filter(Boolean);

    const normalized = tokens.flatMap((name) => {
      const key = name.toLowerCase();
      if (key === 'all') {
        return [...NOTIFICATION_COUNTRY_DISPLAY_GROUPS[NOTIFICATION_COUNTRY_IDS.ALL]];
      }

      return [NOTIFICATION_COUNTRY_NAME_NORMALIZATION[key] ?? name];
    });

    return Array.from(new Set(normalized));
  }

  /** Resets all local notification-related state in one place. */
  private resetAllNotificationState(): void {
    this.notificationsState.set([]);
    this.notificationIdsState.set([]);
    this.totalRecordsState.set(0);
  }

  /** Resets dropdown notifications local state in one place. */
  private resetDropdownNotificationState(): void {
    this.dropdownNotificationsState.set([]);
    this.dropdownNotificationIdsState.set([]);
    this.unreadNotificationIdsState.set([]);
    this.unreadCountState.set(0);
  }

  /** Handles clear-all flow for dropdown notifications. */
  private clearDropdownNotifications(onSuccess?: () => void): void {
    const notificationIds = this.getDropdownClearAllNotificationIds();
    const { countryId, pageSize, userId } = this.lastDropdownQueryState();

    if (!notificationIds.length) {
      this.resetDropdownNotificationState();
      onSuccess?.();
      if (countryId !== null) {
        this.getDropdownNotifications(countryId, pageSize, userId).subscribe();
      }
      return;
    }

    this.http
      .patch<unknown>(this.clearAllApiUrl, { notificationIds })
      .pipe(
        tap(() => {
          this.resetDropdownNotificationState();
          onSuccess?.();
        }),
        switchMap(() => {
          if (countryId === null) {
            return of([]);
          }

          return this.getDropdownNotifications(countryId, pageSize, userId);
        }),
        catchError(() => {
          this.resetDropdownNotificationState();
          return of([]);
        }),
      )
      .subscribe();
  }

  /** Handles clear-all flow for all notifications page. */
  private clearPageNotifications(onSuccess?: () => void): void {
    const notificationIds = this.getClearAllNotificationIds();
    const { countryId, page, pageSize, userId, search } = this.lastPageQueryState();

    if (!notificationIds.length) {
      this.resetAllNotificationState();
      this.unreadCountState.set(0);
      onSuccess?.();
      this.getNotifications(countryId, page, pageSize, userId, search).subscribe();
      return;
    }

    this.http
      .patch<unknown>(this.clearAllApiUrl, { notificationIds })
      .pipe(
        tap(() => {
          this.resetAllNotificationState();
          this.unreadCountState.set(0);
          onSuccess?.();
        }),
        switchMap(() => this.getNotifications(countryId, page, pageSize, userId, search)),
        catchError(() => {
          this.unreadCountState.set(0);
          return of([]);
        }),
      )
      .subscribe();
  }

  /**
   * Type guard that checks whether a value is a plain (non-array, non-null) object.
   * Used to validate response structures before attempting property access.
   */
  private isRecord(value: unknown): value is NotificationApiRecord {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }
}
